import React, { useCallback, useEffect, useState } from "react";
import {
  Button,
  Col,
  Empty,
  Form,
  Input,
  InputNumber,
  List,
  Modal,
  Row,
  Space,
  Spin,
  Tag,
  Typography,
} from "antd";
import { ArrowLeftOutlined, SendOutlined } from "@ant-design/icons";
import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import moment from "moment";
import message from "@/utils/globalMessage";
import { SERVICE_REQUEST_STATUS_COLORS } from "./constants";
import "./styles.css";

export default function ServiceRequestDetail({
  requestId,
  listPath,
  loadRequest,
  addNote,
  canAddNote,
  closeRequest,
  reopenRequest,
  canManageCost = false,
  quoteCost,
  canCreateWorkorder = false,
  createWorkorder,
  getRelatedWorkorderPath,
  respondCost,
}) {
  const navigate = useNavigate();
  const isPC = useSelector((state) => state.App?.isPC ?? true);
  const [record, setRecord] = useState(null);
  const [note, setNote] = useState("");
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [changingStatus, setChangingStatus] = useState(false);
  const [noteModalOpen, setNoteModalOpen] = useState(false);
  const [quoteModalOpen, setQuoteModalOpen] = useState(false);
  const [quotedCost, setQuotedCost] = useState(null);
  const [quoteComment, setQuoteComment] = useState("");
  const [submittingQuote, setSubmittingQuote] = useState(false);
  const [respondingCost, setRespondingCost] = useState(false);
  const [rejectCostModalOpen, setRejectCostModalOpen] = useState(false);
  const [rejectCostComment, setRejectCostComment] = useState("");

  const loadRecord = useCallback(async () => {
    setLoading(true);
    try {
      const response = await loadRequest(requestId);
      setRecord(response.data);
    } catch (error) {
      message.error("Failed to load service request");
    } finally {
      setLoading(false);
    }
  }, [loadRequest, requestId]);

  useEffect(() => {
    loadRecord();
  }, [loadRecord]);

  const submitNote = async () => {
    const content = note.trim();
    if (!content) {
      message.error("Please enter a note");
      return;
    }
    setSubmitting(true);
    try {
      await addNote(requestId, content);
      setNote("");
      setNoteModalOpen(false);
      await loadRecord();
      message.success("Note added successfully");
    } catch (error) {
      message.error(error?.response?.data?.status || "Failed to add note");
    } finally {
      setSubmitting(false);
    }
  };

  const performStatusChange = async () => {
    if (!record || (!closeRequest && !reopenRequest)) return;
    setChangingStatus(true);
    try {
      if (record.status === "closed") {
        await reopenRequest(record.id);
        message.success("Request reopened");
      } else {
        await closeRequest(record.id);
        message.success("Request closed");
      }
      await loadRecord();
    } catch (error) {
      message.error("Failed to update request status");
    } finally {
      setChangingStatus(false);
    }
  };

  const changeStatus = () => {
    if (record?.status === "converted") {
      Modal.confirm({
        title: "Close Service Request?",
        content: "Closing this request will also mark its related work order as completed.",
        okText: "Close Request",
        okType: "danger",
        cancelText: "Cancel",
        onOk: performStatusChange,
      });
      return;
    }
    performStatusChange();
  };

  const submitQuote = async () => {
    if (quotedCost === null || quotedCost === undefined || Number.isNaN(Number(quotedCost))) {
      message.error("Enter a valid USD amount");
      return;
    }
    setSubmittingQuote(true);
    try {
      await quoteCost(record.id, quotedCost, quoteComment.trim() || undefined);
      setQuoteModalOpen(false);
      setQuoteComment("");
      await loadRecord();
      message.success(Number(quotedCost) === 0 ? "Cost requirement removed" : "Cost quote sent to tenant");
    } catch (error) {
      message.error(error?.response?.data?.quoted_cost || "Failed to save cost quote");
    } finally {
      setSubmittingQuote(false);
    }
  };

  const respondToCost = async (decision, comment) => {
    setRespondingCost(true);
    try {
      await respondCost(record.id, decision, comment?.trim() || undefined);
      await loadRecord();
      message.success(decision === "approved" ? "Cost quote approved" : "Cost quote rejected");
      return true;
    } catch (error) {
      message.error(error?.response?.data?.quoted_cost || "Failed to respond to cost quote");
      return false;
    } finally {
      setRespondingCost(false);
    }
  };

  const submitCostRejection = async () => {
    const rejected = await respondToCost("rejected", rejectCostComment);
    if (rejected) {
      setRejectCostModalOpen(false);
      setRejectCostComment("");
    }
  };

  const noteEnabled = Boolean(canAddNote && record?.status !== "closed");
  const canChangeStatus = Boolean(
    record &&
    closeRequest &&
    reopenRequest &&
    (record.status !== "closed" || !record.has_workorder)
  );
  const canQuote = Boolean(
    record &&
    canManageCost &&
    quoteCost &&
    !record.has_workorder &&
    record.status !== "closed" &&
    record.status !== "converted"
  );
  const canConvert = Boolean(
    record &&
    canCreateWorkorder &&
    createWorkorder &&
    !record.has_workorder &&
    record.status !== "closed" &&
    record.status !== "converted" &&
    ["not_required", "approved"].includes(record.cost_approval_status)
  );
  const costStatusLabel = {
    not_required: "No cost approval required",
    pending: "Awaiting tenant approval",
    approved: "Approved",
    rejected: "Rejected",
  }[record?.cost_approval_status] || "No cost approval required";
  const statusButton = canChangeStatus ? (
    <Button
      danger={record.status !== "closed"}
      onClick={changeStatus}
      loading={changingStatus}
    >
      {record.status === "closed" ? "Reopen" : "Close"}
    </Button>
  ) : null;
  const noteButton = (
    <Button
      type="primary"
      icon={<SendOutlined />}
      onClick={() => setNoteModalOpen(true)}
      disabled={!noteEnabled}
    >
      Add Note
    </Button>
  );
  const costButtons = canQuote ? (
    <Button
      onClick={() => {
        setQuotedCost(record.quoted_cost == null ? 0 : Number(record.quoted_cost));
        setQuoteComment("");
        setQuoteModalOpen(true);
      }}
    >
      {record.quoted_cost == null ? "Set Cost" : "Update Cost"}
    </Button>
  ) : null;
  const convertButton = canConvert ? (
    <Button type="primary" onClick={() => createWorkorder(record)}>
      Create Work Order
    </Button>
  ) : null;
  const costResponseButtons = record?.cost_approval_status === "pending" && respondCost ? (
    <Space>
      <Button
        danger
        onClick={() => setRejectCostModalOpen(true)}
        loading={respondingCost}
      >
        Reject Cost
      </Button>
      <Button
        type="primary"
        onClick={() => respondToCost("approved")}
        loading={respondingCost}
      >
        Approve Cost
      </Button>
    </Space>
  ) : null;

  return (
    <div className="service-request-detail-page">
      {isPC && (
        <div className="service-request-detail-header">
          <div className="service-request-detail-heading">
            <Button icon={<ArrowLeftOutlined />} onClick={() => navigate(listPath)} title="Back" />
            <Typography.Title level={4} style={{ margin: 0 }}>
              Service Request {record ? `#${record.id}` : ""}
            </Typography.Title>
            {record && (
              <Tag color={SERVICE_REQUEST_STATUS_COLORS[record.status]}>{record.status}</Tag>
            )}
          </div>
          <Space>
            {noteButton}
            {costButtons}
            {costResponseButtons}
            {convertButton}
            {statusButton}
          </Space>
        </div>
      )}

      <Spin spinning={loading}>
        {record && (
          <>
            {!isPC && (
              <div className="service-request-mobile-status-action">
                <Space wrap>
                  {noteButton}
                  {costButtons}
                  {costResponseButtons}
                  {convertButton}
                  {statusButton}
                </Space>
              </div>
            )}
            <Form className="service-request-overview-form" layout="vertical">
              <Row gutter={[24, 0]}>
                <Col xs={24} md={12}>
                  <Form.Item label="Title">
                    <Input value={record.title} readOnly />
                  </Form.Item>
                </Col>
                <Col xs={24} md={12}>
                  <Form.Item label="Facility">
                    <Input value={record.facility_name} readOnly />
                  </Form.Item>
                </Col>
                <Col xs={24} md={12}>
                  <Form.Item label="Company">
                    <Input value={record.company_name} readOnly />
                  </Form.Item>
                </Col>
                <Col xs={24} md={12}>
                  <Form.Item label="Suite / Area">
                    <Input value={record.suite_area} readOnly />
                  </Form.Item>
                </Col>
                <Col xs={24} md={12}>
                  <Form.Item label="Category">
                    <Input value={record.category_name} readOnly />
                  </Form.Item>
                </Col>
                <Col xs={24} md={12}>
                  <Form.Item label="Priority">
                    <Input value={record.priority} readOnly />
                  </Form.Item>
                </Col>
                <Col xs={24} md={12}>
                  <Form.Item label="Status">
                    <div className="service-request-readonly-field">
                      <Tag color={SERVICE_REQUEST_STATUS_COLORS[record.status]}>{record.status}</Tag>
                    </div>
                  </Form.Item>
                </Col>
                <Col xs={24} md={12}>
                  <Form.Item label="Cost (USD)">
                    <Input
                      value={
                        record.quoted_cost == null
                          ? "No cost"
                          : `$${Number(record.quoted_cost).toFixed(2)}`
                      }
                      readOnly
                    />
                  </Form.Item>
                </Col>
                <Col xs={24} md={12}>
                  <Form.Item label="Cost Approval">
                    <Input value={costStatusLabel} readOnly />
                  </Form.Item>
                </Col>
                {record.related_workorder && (
                  <Col xs={24} md={12}>
                    <Form.Item label="Work Order">
                      {record.related_workorder.is_deleted ? (
                        <Input value="Deleted work order" readOnly />
                      ) : (
                        <Button
                          type="link"
                          className="service-request-related-link"
                          onClick={() => navigate(getRelatedWorkorderPath(record.related_workorder, record))}
                        >
                          {record.related_workorder.workordernumber || `Work Order #${record.related_workorder.id}`}
                        </Button>
                      )}
                    </Form.Item>
                  </Col>
                )}
                <Col xs={24} md={12}>
                  <Form.Item label="Description">
                    <Input.TextArea value={record.description} rows={3} readOnly />
                  </Form.Item>
                </Col>
              </Row>

              <div className="service-request-notes-section">
                <div className="service-request-notes-heading">
                  Notes ({record.notes?.length || 0})
                </div>
                {record.notes?.length ? (
                  <List
                    className="service-request-notes-list"
                    dataSource={record.notes}
                    renderItem={(item) => (
                      <List.Item>
                        <List.Item.Meta
                          title={(
                            <Space wrap>
                              <Typography.Text strong>{item.createdby_name || "Unknown"}</Typography.Text>
                              <Typography.Text type="secondary">
                                {moment(item.created_date).format("YYYY-MM-DD HH:mm")}
                              </Typography.Text>
                            </Space>
                          )}
                          description={(
                            <Typography.Paragraph style={{ marginBottom: 0 }}>
                              {item.content}
                            </Typography.Paragraph>
                          )}
                        />
                      </List.Item>
                    )}
                  />
                ) : (
                  <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="No notes" />
                )}
              </div>

              <Row gutter={[24, 0]} className="service-request-audit-row">
                <Col xs={24} md={12}>
                  <Form.Item label="Requested By">
                    <Input value={record.createdby_name} readOnly />
                  </Form.Item>
                </Col>
                <Col xs={24} md={12}>
                  <Form.Item label="Created Date">
                    <Input
                      value={moment(record.created_date).format("YYYY-MM-DD HH:mm")}
                      readOnly
                    />
                  </Form.Item>
                </Col>
              </Row>
            </Form>

            <Modal
              title="Add Note"
              open={noteModalOpen}
              onCancel={() => {
                setNoteModalOpen(false);
                setNote("");
              }}
              onOk={submitNote}
              okText="Add Note"
              confirmLoading={submitting}
              okButtonProps={{ disabled: !note.trim() }}
              destroyOnClose
            >
              <Input.TextArea
                rows={5}
                value={note}
                onChange={(event) => setNote(event.target.value)}
                placeholder="Enter note"
                maxLength={4000}
                showCount
              />
            </Modal>
            <Modal
              title="Set Cost Quote (USD)"
              open={quoteModalOpen}
              onCancel={() => {
                setQuoteModalOpen(false);
                setQuoteComment("");
              }}
              onOk={submitQuote}
              okText="Send Quote"
              confirmLoading={submittingQuote}
              destroyOnClose
            >
              <InputNumber
                min={0}
                precision={2}
                style={{ width: "100%" }}
                value={quotedCost}
                onChange={setQuotedCost}
                addonBefore="USD"
              />
              <Input.TextArea
                rows={4}
                style={{ marginTop: 16 }}
                value={quoteComment}
                onChange={(event) => setQuoteComment(event.target.value)}
                placeholder="Comment (optional)"
                maxLength={4000}
                showCount
              />
            </Modal>
            <Modal
              title="Reject Cost Quote"
              open={rejectCostModalOpen}
              onCancel={() => {
                setRejectCostModalOpen(false);
                setRejectCostComment("");
              }}
              onOk={submitCostRejection}
              okText="Reject Cost"
              okButtonProps={{ danger: true }}
              confirmLoading={respondingCost}
              destroyOnClose
            >
              <Input.TextArea
                rows={4}
                value={rejectCostComment}
                onChange={(event) => setRejectCostComment(event.target.value)}
                placeholder="Comment (optional)"
                maxLength={4000}
                showCount
              />
            </Modal>
          </>
        )}
      </Spin>
    </div>
  );
}
